import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AppVersion {
  hash: string;
  raw: string;
  dirty: boolean;
  distance: number | null;
  tag: string | null;
  semver: string | null;
  suffix: string;
  semverString: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private readonly VERSION_STORAGE_KEY = 'app_version';

  private currentVersion: AppVersion;
  private versionSubject = new BehaviorSubject<AppVersion | null>(null);

  constructor() {
    this.loadCurrentVersion();
  }

  /**
   * Initialize version checking and cache cleanup
   * Should be called early in app initialization
   */
  public initialize(): void {
    this.checkVersionAndCleanup();
  }

  /**
   * Get current version as observable
   */
  public getVersion(): Observable<AppVersion | null> {
    return this.versionSubject.asObservable();
  }

  /**
   * Get current version synchronously
   */
  public getCurrentVersion(): AppVersion | null {
    return this.currentVersion;
  }

  /**
   * Check if version has changed and clean cache if needed
   */
  private checkVersionAndCleanup(): void {
    const storedVersion = this.getStoredVersion();
    
    if (!storedVersion) {
      // First time user - store current version
      this.storeCurrentVersion();
      console.log('First time user - version stored:', this.currentVersion);
      return;
    }

    if (this.hasVersionChanged(storedVersion)) {
      console.log('Version changed detected:', {
        old: storedVersion,
        new: this.currentVersion
      });
      
      this.cleanupCache();
      this.storeCurrentVersion();
      
      // Optionally show a notification to user
      this.notifyVersionChange();
    } else {
      console.log('Version unchanged, no cleanup needed');
    }
  }

  /**
   * Load current version from git-version.ts
   */
  private loadCurrentVersion(): void {
    try {
      // Import the version data generated by the build process
      const versionModule = require('../../../git-version');
      this.currentVersion = versionModule.gitVersion;
      this.versionSubject.next(this.currentVersion);
    } catch (error) {
      console.warn('Could not load version data:', error);
      this.setFallbackVersion();
    }
  }

  /**
   * Set fallback version for development
   */
  private setFallbackVersion(): void {
    this.currentVersion = {
      hash: 'dev',
      raw: 'dev-version',
      dirty: true,
      distance: null,
      tag: null,
      semver: null,
      suffix: 'dev',
      semverString: null
    };
    this.versionSubject.next(this.currentVersion);
  }

  /**
   * Get stored version from localStorage
   */
  private getStoredVersion(): AppVersion | null {
    try {
      const stored = localStorage.getItem(this.VERSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Could not parse stored version:', error);
      return null;
    }
  }

  /**
   * Store current version in localStorage
   */
  private storeCurrentVersion(): void {
    try {
      localStorage.setItem(this.VERSION_STORAGE_KEY, JSON.stringify(this.currentVersion));
    } catch (error) {
      console.warn('Could not store version:', error);
    }
  }

  /**
   * Check if version has changed
   */
  private hasVersionChanged(storedVersion: AppVersion): boolean {
    if (!this.currentVersion) return false;
    
    // Compare hash as primary identifier
    if (storedVersion.hash !== this.currentVersion.hash) {
      return true;
    }
    
    // Also compare raw version string
    if (storedVersion.raw !== this.currentVersion.raw) {
      return true;
    }
    
    return false;
  }

  /**
   * Clean up all data from localStorage except version tracking and gai-assistant keys
   */
  private cleanupCache(): void {
    console.log('Cleaning up stored data due to version change...');
    
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.shouldRemoveKey(key)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Check if a key should be removed (keep version tracking and gai-assistant keys)
   */
  private shouldRemoveKey(key: string): boolean {
    // Keep version tracking key
    if (key === this.VERSION_STORAGE_KEY) {
      return false;
    }
    
    // Keep gai-assistant keys with number pattern
    if (this.isGaiAssistantKey(key)) {
      return false;
    }
    
    // Remove everything else
    return true;
  }

  /**
   * Check if key matches gai-assistant_{{number}} pattern
   */
  private isGaiAssistantKey(key: string): boolean {
    // Pattern: gai-assistant_ followed by one or more digits
    const gaiAssistantPattern = /^gai-assistant_\d+$/;
    return gaiAssistantPattern.test(key);
  }

  /**
   * Notify user about version change (optional)
   */
  private notifyVersionChange(): void {
    // We can implement a toast notification here
    // For now, just log to console
    console.log('App has been updated to a new version. Cache has been cleared.');
    // Example: Show a toast notification
    // this.toastr.info('App updated! Some cached data has been cleared for the best experience.');
  }
}
